import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { clearCache } from '../utils/localCache';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Safely parse a JWT payload on the client side without external libraries.
 */
export const parseJwt = (token) => {
  if (!token || typeof token !== 'string') return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

/**
 * Returns true if the token is null, invalid, or expired (with a 5s leeway).
 */
export const isTokenExpired = (token) => {
  const decoded = parseJwt(token);
  if (!decoded) return true;
  if (!decoded.exp) return false;
  return decoded.exp * 1000 <= Date.now() + 5000;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const expiryTimerRef = useRef(null);

  const logout = useCallback(async () => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    try {
      const { unregisterPushToken } = require('../utils/nativeBridge');
      await unregisterPushToken();
    } catch (_) {}
    clearCache(); // cached messages/feeds are private to this account
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  }, []);

  const handleSessionExpired = useCallback(async () => {
    const hadToken = !!localStorage.getItem('token');
    if (!hadToken) return;

    console.warn('Session expired. Logging out user...');
    sessionStorage.setItem('bazzef_session_expired', '1');
    await logout();

    const currentPath = window.location.pathname;
    const isPublicAuthPage =
      currentPath.startsWith('/login') ||
      currentPath.startsWith('/register') ||
      currentPath.startsWith('/forgot-password') ||
      currentPath.startsWith('/reset-password');

    if (!isPublicAuthPage) {
      window.location.replace('/login?expired=1');
    }
  }, [logout]);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    if (isTokenExpired(token)) {
      handleSessionExpired();
      return null;
    }

    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await axios.get('/api/auth/me');
      if (response.data) {
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
        return response.data;
      }
    } catch (error) {
      console.error('Failed to refresh user profile:', error);
      const status = error.response?.status;
      const code = error.response?.data?.code;
      if (
        status === 401 ||
        (status === 403 &&
          (code === 'TOKEN_EXPIRED' ||
            code === 'INVALID_TOKEN' ||
            code === 'TOKEN_REVOKED' ||
            code === 'ACCOUNT_DISABLED'))
      ) {
        handleSessionExpired();
      }
    }
    return null;
  }, [handleSessionExpired]);

  const updateUser = (updatedFields) => {
    setUser((prev) => {
      const nextUser = { ...(prev || {}), ...updatedFields };
      localStorage.setItem('user', JSON.stringify(nextUser));
      return nextUser;
    });
  };

  // Schedule auto-logout timer whenever token / user is valid
  useEffect(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }

    const token = localStorage.getItem('token');
    if (!token || !user) return;

    const decoded = parseJwt(token);
    if (!decoded?.exp) return;

    const msUntilExpiry = decoded.exp * 1000 - Date.now();
    if (msUntilExpiry <= 0) {
      handleSessionExpired();
      return;
    }

    if (msUntilExpiry < 2147483647) {
      expiryTimerRef.current = setTimeout(() => {
        handleSessionExpired();
      }, msUntilExpiry);
    }

    return () => {
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = null;
      }
    };
  }, [user, handleSessionExpired]);

  // Check token on visibility change or window focus (e.g., wake from sleep or tab refocus)
  useEffect(() => {
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        const token = localStorage.getItem('token');
        if (token && isTokenExpired(token)) {
          handleSessionExpired();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
    };
  }, [handleSessionExpired]);

  // Global Axios response interceptor: catch 401 & token-expired 403 responses
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error.response?.status;
        const code = error.response?.data?.code;
        const url = error.config?.url || '';

        const isAuthSubmission = [
          '/api/auth/login',
          '/api/auth/register',
          '/api/auth/google',
          '/api/auth/verify-email',
          '/api/auth/resend-verification',
          '/api/auth/forgot-password',
          '/api/auth/reset-password'
        ].some((endpoint) => url.includes(endpoint));

        const isAuthError =
          status === 401 ||
          (status === 403 &&
            (code === 'TOKEN_EXPIRED' ||
              code === 'INVALID_TOKEN' ||
              code === 'TOKEN_REVOKED' ||
              code === 'ACCOUNT_DISABLED'));

        if (isAuthError && !isAuthSubmission) {
          const hadToken = !!localStorage.getItem('token');
          if (hadToken) {
            handleSessionExpired();
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [handleSessionExpired]);

  // Initial authentication hydration
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (token) {
      if (isTokenExpired(token)) {
        console.warn('Authentication token expired on startup. Logging out...');
        sessionStorage.setItem('bazzef_session_expired', '1');
        clearCache();
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        setLoading(false);
        return;
      }

      if (userData && userData !== 'undefined' && userData !== 'null') {
        try {
          setUser(JSON.parse(userData));
        } catch (e) {
          console.error('Failed to parse cached user:', e);
          localStorage.removeItem('user');
        }
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      refreshUser();
    }
    setLoading(false);
  }, [refreshUser]);

  const login = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', { email, password });
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);

      return { success: true };
    } catch (error) {
      const isUnverified = error.response?.data?.code === 'EMAIL_NOT_VERIFIED';
      const message =
        error.response?.data?.error ||
        (error.code === 'ERR_NETWORK' || !error.response
          ? 'Cannot reach server. Check your connection and that the app is configured with the correct API URL.'
          : 'Login failed');
      return {
        success: false,
        error: message,
        requireVerification: isUnverified,
        email: error.response?.data?.email || email
      };
    }
  };

  const register = async (username, email, password, handle, options = {}) => {
    try {
      const {
        accountType = 'individual',
        organizationDisplayName,
        organizationCategory,
        website,
        acceptTerms
      } = options;
      const response = await axios.post('/api/auth/register', {
        username,
        email,
        password,
        handle,
        acceptTerms: acceptTerms === true,
        accountType,
        organizationDisplayName: accountType === 'organization' ? organizationDisplayName : undefined,
        organizationCategory: accountType === 'organization' ? organizationCategory : undefined,
        website: website || undefined
      });

      if (response.data.requireVerification) {
        return {
          success: true,
          requireVerification: true,
          email: response.data.email,
          message: response.data.message
        };
      }

      const { token, user } = response.data;
      if (token && user) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(user);
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed'
      };
    }
  };

  const verifyEmail = async (email, code) => {
    try {
      const response = await axios.post('/api/auth/verify-email', { email, code });
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);

      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Verification failed. Please check your code.'
      };
    }
  };

  const resendVerification = async (email) => {
    try {
      const response = await axios.post('/api/auth/resend-verification', { email });
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to resend verification code'
      };
    }
  };

  const loginWithGoogle = async ({ credential, accessToken, demoUser, acceptTerms } = {}) => {
    try {
      const response = await axios.post('/api/auth/google', {
        credential,
        accessToken,
        demoUser,
        acceptTerms: acceptTerms === true
      });
      // A first-time Google sign-up: no account exists until the terms are accepted
      if (response.data.needsTerms) {
        return { success: false, needsTerms: true, profile: response.data.profile };
      }
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);

      return { success: true, user };
    } catch (error) {
      const message =
        error.response?.data?.error ||
        (error.code === 'ERR_NETWORK' || !error.response
          ? 'Cannot reach server. Check your connection.'
          : 'Google authentication failed');
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    login,
    register,
    verifyEmail,
    resendVerification,
    loginWithGoogle,
    logout,
    loading,
    refreshUser,
    updateUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
